/**
 * AdVideoBuilder – UI for generating AI video ads.
 * Prompt field, model selector, aspect ratio, duration.
 * Uses SSE streaming to show real-time generation progress.
 */
import { useState, useCallback, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getAuthToken, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { usdToTokens } from "@shared/ugc-pricing";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Loader2,
  Film,
  Download,
  Trash2,
  Wand2,
  AlertCircle,
  CheckCircle2,
  Clock,
  Zap,
  Image as ImageIcon,
  Upload,
  X,
  History,
  BookOpen,
  MousePointerClick,
  Hand,
  Sparkles,
  ChevronRight,
  Check,
  Search,
  FolderOpen,
  Video,
  Music,
  AtSign,
  Volume2,
  VolumeX,
  Smartphone,
  Gem,
} from "lucide-react";
import HeyGenAvatarPicker from "@/components/heygen-avatar-picker";

// ── Types ─────────────────────────────────────────────────

type VideoModel =
  | "kling-2.1"
  | "ltx-video"
  | "seedance-2.0"
  | "kling-motion"
  | "kling-motion-standard"
  | "heygen-avatar"
  | "veo-3.1"
  | "veo-3.1-fast"
  | "kling-o1-edit"
  | "grok-imagine-edit";
type ModelCategory = "create" | "edit" | "motion";
type VideoAspectRatio = "16:9" | "9:16" | "1:1" | "21:9" | "4:3" | "3:4" | "auto";
type VideoDuration = number;
type VideoResolution = "480p" | "720p";

interface HeyGenAvatar {
  id: string;
  avatar_name: string;
  preview_image_url: string;
  preview_video_url?: string;
  avatar_type: string;
}

interface HeyGenVoice {
  voice_id: string;
  name: string;
  language: string;
  gender: string;
}

type HeyGenAspect = "16:9" | "9:16" | "1:1";
type HeyGenResolution = "720p" | "1080p" | "4k";
type HeyGenAvatarStyle = "normal" | "closeUp" | "circle";
type HeyGenExpressiveness = "low" | "medium" | "high";

interface GeneratedVideo {
  id: string;
  videoUrl: string;
  prompt: string;
  model: string;
  durationSeconds: number;
  aspectRatio: string;
  fileSizeBytes: number;
}

type GenerationStatus = "idle" | "queued" | "in_progress" | "completed" | "error";

interface VideoHistoryItem {
  id: string;
  signedUrl: string | null;
  prompt: string | null;
  imageModel: string | null;
  fileSizeBytes: number | null;
  createdAt: string | null;
}

interface GenerationState {
  status: GenerationStatus;
  message?: string;
  queuePosition?: number;
}

// ── Constants ─────────────────────────────────────────────

const VIDEO_MODELS: {
  id: VideoModel;
  label: string;
  badge?: string;
  desc: string;
  maxDuration: VideoDuration;
  minDuration: VideoDuration;
  category: ModelCategory;
}[] = [
  {
    id: "veo-3.1",
    label: "Veo 3.1",
    badge: "Google · z audio",
    desc: "Najwyższa jakość z natywnym audio (Google Veo 3.1). 4/6/8s.",
    minDuration: 4,
    maxDuration: 8,
    category: "create",
  },
  {
    id: "veo-3.1-fast",
    label: "Veo 3.1 Fast",
    badge: "Google · szybsze · tańsze",
    desc: "Veo 3.1 w wersji fast — szybsze i tańsze, z natywnym audio. 4/6/8s.",
    minDuration: 4,
    maxDuration: 8,
    category: "create",
  },
  {
    id: "seedance-2.0",
    label: "Seedance 2.0",
    badge: "models.seedance20.badge",
    desc: "models.seedance20.desc",
    minDuration: 4,
    maxDuration: 15,
    category: "create",
  },
  {
    id: "kling-motion",
    label: "Motion Control Pro",
    badge: "models.klingMotion.badge",
    desc: "models.klingMotion.desc",
    minDuration: 5,
    maxDuration: 30,
    category: "motion",
  },
  {
    id: "kling-motion-standard",
    label: "Motion Control Standard",
    badge: "models.klingMotionStandard.badge",
    desc: "models.klingMotionStandard.desc",
    minDuration: 5,
    maxDuration: 30,
    category: "motion",
  },
  {
    id: "kling-2.1",
    label: "Kling 2.1",
    badge: "models.kling21.badge",
    desc: "models.kling21.desc",
    minDuration: 5,
    maxDuration: 10,
    category: "create",
  },
  {
    id: "ltx-video",
    label: "LTX Video",
    badge: "models.ltxVideo.badge",
    desc: "models.ltxVideo.desc",
    minDuration: 5,
    maxDuration: 5,
    category: "create",
  },
  {
    id: "heygen-avatar",
    label: "Avatar AI (HeyGen)",
    badge: "models.heygenAvatar.badge",
    desc: "models.heygenAvatar.desc",
    minDuration: 5,
    maxDuration: 120,
    category: "create",
  },
  {
    id: "kling-o1-edit",
    label: "Kling O1 Video Edit",
    badge: "Generuj z elementami i referencjami",
    desc: "Edytuj wideo (3–10s, MP4/MOV) promptem. Wstaw @Image1, @Image2 by odnieść się do obrazów referencyjnych.",
    minDuration: 3,
    maxDuration: 10,
    category: "edit",
  },
  {
    id: "grok-imagine-edit",
    label: "Grok Imagine Edit",
    badge: "xAI · edycja wideo promptem",
    desc: "Szybka edycja wideo promptem — zmiana stylu, kolorów, atmosfery (xAI Grok Imagine).",
    minDuration: 3,
    maxDuration: 10,
    category: "edit",
  },
];

const ASPECT_RATIOS: {
  id: VideoAspectRatio;
  label: string;
  icon: string;
  desc: string;
  models: VideoModel[];
}[] = [
  {
    id: "9:16",
    label: "9:16",
    icon: "📱",
    desc: "Story / Reels",
    models: [
      "kling-2.1",
      "ltx-video",
      "seedance-2.0",
      "kling-motion",
      "kling-motion-standard",
      "heygen-avatar",
      "veo-3.1",
      "veo-3.1-fast",
    ],
  },
  {
    id: "16:9",
    label: "16:9",
    icon: "🖥️",
    desc: "YouTube / Landscape",
    models: [
      "kling-2.1",
      "ltx-video",
      "seedance-2.0",
      "kling-motion",
      "kling-motion-standard",
      "heygen-avatar",
      "veo-3.1",
      "veo-3.1-fast",
    ],
  },
  {
    id: "1:1",
    label: "1:1",
    icon: "⬜",
    desc: "Feed / Square",
    models: [
      "kling-2.1",
      "ltx-video",
      "seedance-2.0",
      "kling-motion",
      "kling-motion-standard",
      "heygen-avatar",
    ],
  },
  { id: "4:3", label: "4:3", icon: "🖼️", desc: "Classic", models: ["seedance-2.0"] },
  { id: "3:4", label: "3:4", icon: "📷", desc: "Portrait", models: ["seedance-2.0"] },
  { id: "21:9", label: "21:9", icon: "🎬", desc: "Ultrawide", models: ["seedance-2.0"] },
  {
    id: "auto",
    label: "Auto",
    icon: "✨",
    desc: "aspectRatio.modelChooses",
    models: ["seedance-2.0", "veo-3.1", "veo-3.1-fast"],
  },
];

const RESOLUTIONS: { id: VideoResolution; label: string; desc: string }[] = [
  { id: "480p", label: "480p", desc: "resolution.480p" },
  { id: "720p", label: "720p", desc: "resolution.720p" },
];

const TOKEN_COSTS_KLING_LTX: Record<"kling-2.1" | "ltx-video", Record<5 | 10, number>> = {
  "kling-2.1": { 5: 6000, 10: 10000 },
  "ltx-video": { 5: 3000, 10: 5000 },
};

const TOKEN_COSTS_KLING_MOTION: Record<5 | 10 | 30, number> = {
  5: 8000,
  10: 14000,
  30: 40000,
};

const TOKEN_COSTS_KLING_MOTION_STANDARD: Record<5 | 10 | 30, number> = {
  5: 4000,
  10: 7000,
  30: 20000,
};

// Motion Control is billed per-second of input driving video
const KLING_MOTION_PRO_PER_SECOND = 1400; // ~14k / 10s ≈ 1400 Claude
const KLING_MOTION_STANDARD_PER_SECOND = 700; // ~50% taniej

const CLAUDE_PER_APP = 240;

const SEEDANCE_COST_PER_SECOND: Record<VideoResolution, number> = {
  "480p": 4000,
  "720p": 8000,
};

/**
 * Single source of truth — same formula as backend `computeVideoCostUsd` →
 * `usdToTokens()` from `@shared/ugc-pricing`. Returns the actual app tokens
 * the user will be charged (post-margin).
 */
function computeAppTokenCost(params: {
  model: VideoModel;
  duration: VideoDuration;
  resolution: VideoResolution;
}): number {
  if (params.model === "heygen-avatar") return 0;
  const usd = computeVideoUsd(params);
  if (usd == null) return 0;
  return usdToTokens(usd);
}

function computeVideoUsd(params: {
  model: VideoModel;
  duration: VideoDuration;
  resolution: VideoResolution;
}): number | null {
  if (params.model === "seedance-2.0") {
    const dur = Math.max(4, Math.min(15, params.duration));
    const perSec = params.resolution === "480p" ? 0.1345 : 0.3024;
    return perSec * dur;
  }
  if (params.model === "kling-2.1") {
    const dur = params.duration === 10 ? 10 : 5;
    return 0.07 * dur;
  }
  if (params.model === "ltx-video") {
    const dur = params.duration === 10 ? 10 : 5;
    return 0.02 * dur;
  }
  if (params.model === "kling-motion" || params.model === "kling-motion-standard") {
    const secs = Math.max(1, Math.min(30, Math.ceil(params.duration)));
    const perSec = params.model === "kling-motion" ? 0.3 : 0.1;
    return perSec * secs;
  }
  if (params.model === "veo-3.1" || params.model === "veo-3.1-fast") {
    // Veo 3.1 fal.ai: standard $0.40/s with audio, fast $0.15/s with audio.
    const allowed = [4, 6, 8] as const;
    const dur = allowed.reduce((best, cur) =>
      Math.abs(cur - params.duration) < Math.abs(best - params.duration) ? cur : best
    );
    const perSec = params.model === "veo-3.1" ? 0.4 : 0.15;
    return perSec * dur;
  }
  return null;
}

function computeTokenCost(params: {
  model: VideoModel;
  duration: VideoDuration;
  resolution: VideoResolution;
}): number {
  // Backwards-compat alias — legacy "Claude tokens" callsites map to app tokens
  // via the new pricing pipeline.
  return computeAppTokenCost(params);
}

const PROMPT_EXAMPLES = [
  "Profesjonalna reklama produktu kosmetycznego, elegancka kobieta nakłada krem na twarz, miękkie światło studyjne, ciepłe kolory, slow motion",
  "Dynamiczna reklama suplementu diety, sportowiec biegnie przez las, energetyczna muzyka, szybkie cięcia, neonowe akcenty",
  "Reklama kursu online, animowany tekst z benefitami kursu, czyste białe tło, nowoczesna typografia, przycisk CTA na końcu",
  "Reklama restauracji, zbliżenie na apetyczne danie, para przy stole, ciepłe oświetlenie, kinematograficzne ujęcie",
];

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function StatusBadge({ state }: { state: GenerationState }) {
  const { t } = useTranslation("adVideoBuilder");
  const configs: Record<GenerationStatus, { icon: React.ReactNode; label: string; color: string }> =
    {
      idle: { icon: null, label: "", color: "" },
      queued: {
        icon: <Clock className="w-3.5 h-3.5 animate-pulse" />,
        label: t("statusBadge.queued"),
        color: "text-yellow-400",
      },
      in_progress: {
        icon: <Loader2 className="w-3.5 h-3.5 animate-spin" />,
        label: t("statusBadge.inProgress"),
        color: "text-blue-400",
      },
      completed: {
        icon: <CheckCircle2 className="w-3.5 h-3.5" />,
        label: t("statusBadge.completed"),
        color: "text-emerald-400",
      },
      error: {
        icon: <AlertCircle className="w-3.5 h-3.5" />,
        label: t("statusBadge.error"),
        color: "text-red-400",
      },
    };

  const cfg = configs[state.status];
  if (!cfg.label) return null;

  return (
    <div className={`flex items-center gap-1.5 text-xs font-medium ${cfg.color}`}>
      {cfg.icon}
      <span>{cfg.label}</span>
      {state.status === "queued" && state.queuePosition != null && (
        <span className="text-white/40">
          {t("statusBadge.position", { position: state.queuePosition })}
        </span>
      )}
      {state.message && state.status !== "queued" && (
        <span className="text-white/40">· {state.message}</span>
      )}
    </div>
  );
}

function VideoCard({ video, onDelete }: { video: GeneratedVideo; onDelete: (id: string) => void }) {
  const { t } = useTranslation("adVideoBuilder");
  return (
    <div className="rounded-xl border border-white/[0.08] bg-[#0d0f14] overflow-hidden">
      <div className="relative bg-black/40 aspect-video flex items-center justify-center">
        <video
          src={video.videoUrl}
          controls
          className="w-full h-full object-contain"
          preload="metadata"
        />
      </div>
      <div className="p-3 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 text-xs text-white/50">
            <Film className="w-3 h-3" />
            <span>{video.model}</span>
            <span>·</span>
            <span>{video.aspectRatio}</span>
            <span>·</span>
            <span>{video.durationSeconds}s</span>
            <span>·</span>
            <span>{formatBytes(video.fileSizeBytes)}</span>
          </div>
          <div className="flex gap-1">
            {video.videoUrl && (
              <a
                href={video.videoUrl}
                download={`video-ad-${video.id.slice(0, 8)}.mp4`}
                className="p-1.5 rounded text-white/30 hover:text-white/70 transition-colors"
                title="Pobierz"
              >
                <Download className="w-3.5 h-3.5" />
              </a>
            )}
            <button
              onClick={() => onDelete(video.id)}
              className="p-1.5 rounded text-white/30 hover:text-red-400 transition-colors"
              title={t("results.deleteTooltip")}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
        {video.prompt && (
          <p className="text-[10px] text-white/30 leading-relaxed line-clamp-2">{video.prompt}</p>
        )}
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────

interface AdVideoBuilderProps {
  initialReferenceImageUrl?: string;
  initialReferenceImageName?: string;
}

export default function AdVideoBuilder({
  initialReferenceImageUrl,
  initialReferenceImageName,
}: AdVideoBuilderProps) {
  const { t } = useTranslation("adVideoBuilder");
  const { toast } = useToast();
  const qc = useQueryClient();

  const [prompt, setPrompt] = useState("");
  const [selectedModel, setSelectedModel] = useState<VideoModel>("kling-2.1");
  const [aspectRatio, setAspectRatio] = useState<VideoAspectRatio>("9:16");
  const [duration, setDuration] = useState<VideoDuration>(5);
  const [negativePrompt, setNegativePrompt] = useState("");
  const [referenceImageBase64, setReferenceImageBase64] = useState<string | undefined>(
    initialReferenceImageUrl
  );
  const [referenceImageName, setReferenceImageName] = useState<string>(
    initialReferenceImageName ?? ""
  );
  // Seedance-specific state
  const [resolution, setResolution] = useState<VideoResolution>("720p");
  const [generateAudio, setGenerateAudio] = useState<boolean>(true);
  const [seedanceMode, setSeedanceMode] = useState<"auto" | "i2v" | "reference">("auto");
  const [seedanceImages, setSeedanceImages] = useState<
    { url: string; name: string; previewUrl: string }[]
  >([]);
  const [seedanceVideos, setSeedanceVideos] = useState<{ url: string; name: string }[]>([]);
  const [seedanceAudios, setSeedanceAudios] = useState<{ url: string; name: string }[]>([]);
  const [assetUploading, setAssetUploading] = useState<"image" | "video" | "audio" | null>(null);
  // Motion Control state
  const [motionCharacterImage, setMotionCharacterImage] = useState<{
    url: string;
    name: string;
    previewUrl: string;
  } | null>(null);
  const [motionDrivingVideo, setMotionDrivingVideo] = useState<{
    url: string;
    name: string;
    durationSeconds: number;
  } | null>(null);
  const [motionUploading, setMotionUploading] = useState<"character" | "driving" | null>(null);
  const [motionConsent, setMotionConsent] = useState<boolean>(false);
  const [motionOrientation, setMotionOrientation] = useState<"image" | "video">("image");

  // HeyGen avatar state
  const [heygenScript, setHeygenScript] = useState("");
  const [heygenAvatarId, setHeygenAvatarId] = useState<string>("");
  const [heygenVoiceId, setHeygenVoiceId] = useState<string>("");
  const [heygenVoices, setHeygenVoices] = useState<HeyGenVoice[]>([]);
  const [heygenLanguageFilter, setHeygenLanguageFilter] = useState<string>("polish");
  const [heygenLoading, setHeygenLoading] = useState<boolean>(false);
  const [heygenResolution, setHeygenResolution] = useState<HeyGenResolution>("1080p");
  const [heygenAvatarStyle, setHeygenAvatarStyle] = useState<HeyGenAvatarStyle>("normal");
  const [heygenExpressiveness, setHeygenExpressiveness] = useState<HeyGenExpressiveness>("medium");
  const [heygenCaption, setHeygenCaption] = useState<boolean>(false);

  const [generationState, setGenerationState] = useState<GenerationState>({ status: "idle" });
  const [generatedVideos, setGeneratedVideos] = useState<GeneratedVideo[]>([]);
  const [resultsTab, setResultsTab] = useState<"current" | "history" | "how-it-works">(
    "how-it-works"
  );
  const historyView = resultsTab === "history";
  const [modelDialogOpen, setModelDialogOpen] = useState(false);
  const [modelSearch, setModelSearch] = useState("");
  const [libraryPickerOpen, setLibraryPickerOpen] = useState(false);
  const [topTab, setTopTab] = useState<"create" | "edit" | "motion">("create");
  const unifiedMediaInputRef = useRef<HTMLInputElement>(null);
  const editVideoInputRef = useRef<HTMLInputElement>(null);
  const [editVideoUrl, setEditVideoUrl] = useState<string | null>(null);
  const [editVideoName, setEditVideoName] = useState<string>("");
  const [autoSettings, setAutoSettings] = useState<boolean>(true);

  useEffect(() => {
    if (generationState.status !== "idle" && resultsTab === "how-it-works") {
      setResultsTab("current");
    }
  }, [generationState.status, resultsTab]);

  useEffect(() => {
    const meta = VIDEO_MODELS.find((m) => m.id === selectedModel);
    if (meta) setTopTab(meta.category);
  }, [selectedModel]);

  const { data: historyData, isLoading: historyLoading } = useQuery<{ items: VideoHistoryItem[] }>({
    queryKey: ["video-ad-history"],
    queryFn: () => apiRequest("GET", "/api/graphics?mediaType=video"),
    staleTime: 30000,
    enabled: historyView,
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const seedanceImagesInputRef = useRef<HTMLInputElement>(null);
  const seedanceVideosInputRef = useRef<HTMLInputElement>(null);
  const seedanceAudiosInputRef = useRef<HTMLInputElement>(null);
  const motionCharacterInputRef = useRef<HTMLInputElement>(null);
  const motionDrivingInputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const currentModel = VIDEO_MODELS.find((m) => m.id === selectedModel)!;
  const isSeedance = selectedModel === "seedance-2.0";
  const isMotion = selectedModel === "kling-motion" || selectedModel === "kling-motion-standard";
  const isVeo = selectedModel === "veo-3.1" || selectedModel === "veo-3.1-fast";
  const isHeygen = selectedModel === "heygen-avatar";
  const isEdit = selectedModel === "kling-o1-edit" || selectedModel === "grok-imagine-edit";
  const tokenCost = computeTokenCost({ model: selectedModel, duration, resolution });
  const isGenerating =
    generationState.status === "queued" || generationState.status === "in_progress";
  const availableAspectRatios = ASPECT_RATIOS.filter((r) => r.models.includes(selectedModel));

  // Motion Control: duration = actual length of user's recording (auto-sync)
  useEffect(() => {
    if (!isMotion || !motionDrivingVideo) return;
    const secs = Math.ceil(motionDrivingVideo.durationSeconds);
    setDuration(secs);
  }, [isMotion, motionDrivingVideo]);

  // HeyGen: load voices on first activation (avatars are managed by HeyGenAvatarPicker)
  useEffect(() => {
    if (!isHeygen) return;
    if (heygenVoices.length > 0) return;

    let cancelled = false;
    const token = getAuthToken();
    const headers = { Authorization: `Bearer ${token ?? ""}` };

    setHeygenLoading(true);
    fetch(`/api/ad-creator/heygen/voices?language=${encodeURIComponent(heygenLanguageFilter)}`, {
      headers,
    })
      .then((r) => r.json())
      .then((voicesRes) => {
        if (cancelled) return;
        const voices = (voicesRes?.voices ?? []) as HeyGenVoice[];
        setHeygenVoices(voices);
        if (!heygenVoiceId && voices.length > 0) setHeygenVoiceId(voices[0].voice_id);
      })
      .catch((err) => {
        if (cancelled) return;
        toast({
          title: t("toasts.heygenVoicesFailed"),
          description: (err as Error).message || t("toasts.heygenVoicesDesc"),
          variant: "destructive",
        });
      })
      .finally(() => {
        if (!cancelled) setHeygenLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isHeygen, heygenLanguageFilter, heygenVoices.length, heygenVoiceId, toast]);

  // Clamp duration & aspect ratio when model changes
  const handleModelChange = useCallback((model: VideoModel) => {
    setSelectedModel(model);
    const cfg = VIDEO_MODELS.find((m) => m.id === model)!;
    setDuration((prev) => {
      const isVeoModel = model === "veo-3.1" || model === "veo-3.1-fast";
      if (isVeoModel) {
        const allowed = [4, 6, 8] as const;
        return allowed.reduce((best, cur) =>
          Math.abs(cur - prev) < Math.abs(best - prev) ? cur : best
        );
      }
      if (prev < cfg.minDuration) return cfg.minDuration;
      if (prev > cfg.maxDuration) return cfg.maxDuration;
      return prev;
    });
    // If current aspect ratio is not supported by new model, fall back to the
    // first allowed ratio (not hardcoded 9:16 — Motion Control supports both
    // orientations and we'd jar the user otherwise).
    setAspectRatio((prev) => {
      const allowed = ASPECT_RATIOS.filter((r) => r.models.includes(model)).map((r) => r.id);
      return allowed.includes(prev) ? prev : (allowed[0] ?? "9:16");
    });
    // Clear stale assets that don't belong to the new model — otherwise leftover
    // referenceImage/motion video gets re-sent and Meta/fal rejects the call.
    const isMotionTarget = model === "kling-motion" || model === "kling-motion-standard";
    const isHeygenTarget = model === "heygen-avatar";
    if (!isMotionTarget) {
      setMotionCharacterImage(null);
      setMotionDrivingVideo(null);
    }
    if (model !== "seedance-2.0") {
      setSeedanceImages([]);
      setSeedanceVideos([]);
      setSeedanceAudios([]);
    }
    const acceptsRefImage =
      model === "kling-2.1" || model === "veo-3.1" || model === "veo-3.1-fast";
    if (!acceptsRefImage && !isMotionTarget && !isHeygenTarget) {
      setReferenceImageBase64(undefined);
      setReferenceImageName("");
    }
  }, []);

  const uploadSeedanceAsset = useCallback(
    async (file: File, assetType: "image" | "video" | "audio"): Promise<string> => {
      const token = getAuthToken();
      const form = new FormData();
      form.append("file", file);
      form.append("assetType", assetType);
      const resp = await fetch("/api/ad-creator/seedance-asset", {
        method: "POST",
        headers: { Authorization: `Bearer ${token ?? ""}` },
        body: form,
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ message: `HTTP ${resp.status}` }));
        throw new Error(err.message || t("toasts.uploadRetry"));
      }
      const data = (await resp.json()) as { url: string };
      return data.url;
    },
    []
  );

  const handleReferenceImageUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      e.target.value = "";
      setAssetUploading("image");
      try {
        const url = await uploadSeedanceAsset(file, "image");
        setReferenceImageBase64(url);
        setReferenceImageName(file.name);
      } catch (err) {
        toast({
          title: t("toasts.uploadRetry"),
          description: t("toasts.uploadRetryDesc"),
          variant: "destructive",
        });
      } finally {
        setAssetUploading(null);
      }
    },
    [uploadSeedanceAsset, toast]
  );

  const handleSeedanceImagesUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      e.target.value = "";
      if (!files.length) return;
      const remaining = 9 - seedanceImages.length;
      const toUpload = files.slice(0, remaining);

      setAssetUploading("image");
      try {
        const uploads = await Promise.all(
          toUpload.map(async (file) => {
            const url = await uploadSeedanceAsset(file, "image");
            return { url, name: file.name, previewUrl: URL.createObjectURL(file) };
          })
        );
        setSeedanceImages((prev) => [...prev, ...uploads].slice(0, 9));
      } catch (err) {
        toast({
          title: t("toasts.uploadRetry"),
          description: t("toasts.uploadRetryDesc"),
          variant: "destructive",
        });
      } finally {
        setAssetUploading(null);
      }
    },
    [seedanceImages.length, uploadSeedanceAsset, toast]
  );

  const handleSeedanceVideosUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      e.target.value = "";
      if (!files.length) return;
      const remaining = 3 - seedanceVideos.length;
      const toUpload = files.slice(0, remaining);

      setAssetUploading("video");
      try {
        const uploads = await Promise.all(
          toUpload.map(async (file) => {
            const url = await uploadSeedanceAsset(file, "video");
            return { url, name: file.name };
          })
        );
        setSeedanceVideos((prev) => [...prev, ...uploads].slice(0, 3));
      } catch (err) {
        toast({
          title: t("toasts.uploadRetry"),
          description: t("toasts.uploadRetryDesc"),
          variant: "destructive",
        });
      } finally {
        setAssetUploading(null);
      }
    },
    [seedanceVideos.length, uploadSeedanceAsset, toast]
  );

  const handleSeedanceAudiosUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      e.target.value = "";
      if (!files.length) return;
      const remaining = 3 - seedanceAudios.length;
      const toUpload = files.slice(0, remaining);

      setAssetUploading("audio");
      try {
        const uploads = await Promise.all(
          toUpload.map(async (file) => {
            const url = await uploadSeedanceAsset(file, "audio");
            return { url, name: file.name };
          })
        );
        setSeedanceAudios((prev) => [...prev, ...uploads].slice(0, 3));
      } catch (err) {
        toast({
          title: t("toasts.uploadRetry"),
          description: t("toasts.uploadRetryDesc"),
          variant: "destructive",
        });
      } finally {
        setAssetUploading(null);
      }
    },
    [seedanceAudios.length, uploadSeedanceAsset, toast]
  );

  const removeSeedanceImage = useCallback((index: number) => {
    setSeedanceImages((prev) => {
      const target = prev[index];
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const handleEditVideoUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file) return;
      setAssetUploading("video");
      try {
        const url = await uploadSeedanceAsset(file, "video");
        setEditVideoUrl(url);
        setEditVideoName(file.name);
      } catch (err) {
        toast({
          title: "Błąd wgrywania wideo",
          description: (err as Error).message,
          variant: "destructive",
        });
      } finally {
        setAssetUploading(null);
      }
    },
    [uploadSeedanceAsset, toast]
  );

  const handleMotionCharacterUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file) return;
      setMotionUploading("character");
      try {
        const url = await uploadSeedanceAsset(file, "image");
        if (motionCharacterImage?.previewUrl) URL.revokeObjectURL(motionCharacterImage.previewUrl);
        setMotionCharacterImage({ url, name: file.name, previewUrl: URL.createObjectURL(file) });
      } catch (err) {
        toast({
          title: t("toasts.characterUploadError"),
          description: (err as Error).message,
          variant: "destructive",
        });
      } finally {
        setMotionUploading(null);
      }
    },
    [uploadSeedanceAsset, motionCharacterImage, toast]
  );

  const handleMotionDrivingUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file) return;

      // Read video duration locally before upload
      const durationSeconds = await new Promise<number>((resolve, reject) => {
        const video = document.createElement("video");
        video.preload = "metadata";
        video.onloadedmetadata = () => {
          URL.revokeObjectURL(video.src);
          resolve(video.duration);
        };
        video.onerror = () => {
          URL.revokeObjectURL(video.src);
          reject(new Error(t("toasts.videoMetaError")));
        };
        video.src = URL.createObjectURL(file);
      }).catch((err) => {
        toast({
          title: t("toasts.generationError"),
          description: (err as Error).message,
          variant: "destructive",
        });
        return null;
      });

      if (durationSeconds === null) return;

      if (durationSeconds > 30.5) {
        toast({
          title: t("toasts.tooLong"),
          description: t("toasts.tooLongDesc", { duration: durationSeconds.toFixed(1) }),
          variant: "destructive",
        });
        return;
      }

      setMotionUploading("driving");
      try {
        const url = await uploadSeedanceAsset(file, "video");
        setMotionDrivingVideo({ url, name: file.name, durationSeconds });
      } catch (err) {
        toast({
          title: t("toasts.drivingUploadError"),
          description: (err as Error).message,
          variant: "destructive",
        });
      } finally {
        setMotionUploading(null);
      }
    },
    [uploadSeedanceAsset, toast]
  );

  const { data: libraryData, isLoading: libraryLoading } = useQuery<{
    items: Array<{
      id: string;
      filename: string;
      signedUrl: string | null;
      mediaType: "image" | "video" | null;
      format: string;
    }>;
  }>({
    queryKey: ["ad-video-builder-library-picker"],
    queryFn: () => apiRequest("GET", "/api/graphics?pageSize=60"),
    enabled: libraryPickerOpen,
    staleTime: 30_000,
  });

  const handleLibrarySelect = useCallback(
    async (item: {
      signedUrl: string | null;
      filename: string;
      mediaType: "image" | "video" | null;
    }) => {
      if (!item.signedUrl || !item.mediaType) return;
      const isImage = item.mediaType === "image";
      const isVideo = item.mediaType === "video";
      setLibraryPickerOpen(false);
      setAssetUploading(isImage ? "image" : "video");
      try {
        const resp = await fetch(item.signedUrl);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const blob = await resp.blob();
        const file = new File([blob], item.filename, { type: blob.type });
        const url = await uploadSeedanceAsset(file, isImage ? "image" : "video");

        if (isMotion) {
          if (isImage) {
            if (motionCharacterImage?.previewUrl)
              URL.revokeObjectURL(motionCharacterImage.previewUrl);
            setMotionCharacterImage({
              url,
              name: file.name,
              previewUrl: URL.createObjectURL(file),
            });
          } else if (isVideo) {
            const durationSeconds = await new Promise<number>((resolve, reject) => {
              const v = document.createElement("video");
              v.preload = "metadata";
              v.onloadedmetadata = () => {
                URL.revokeObjectURL(v.src);
                resolve(v.duration);
              };
              v.onerror = () => {
                URL.revokeObjectURL(v.src);
                reject(new Error("meta"));
              };
              v.src = URL.createObjectURL(file);
            }).catch(() => 0);
            if (durationSeconds > 30.5) {
              toast({
                title: t("toasts.tooLong"),
                description: t("toasts.tooLongDesc", { duration: durationSeconds.toFixed(1) }),
                variant: "destructive",
              });
              return;
            }
            setMotionDrivingVideo({ url, name: file.name, durationSeconds });
          }
        } else if (isSeedance) {
          if (isImage) {
            setSeedanceImages((prev) =>
              [...prev, { url, name: file.name, previewUrl: URL.createObjectURL(file) }].slice(0, 9)
            );
          } else if (isVideo) {
            setSeedanceVideos((prev) =>
              [...prev, { url, name: file.name, previewUrl: URL.createObjectURL(file) }].slice(0, 3)
            );
          }
        } else if ((selectedModel === "kling-2.1" || isVeo) && isImage) {
          setReferenceImageBase64(url);
          setReferenceImageName(file.name);
        }
      } catch (err) {
        toast({
          title: t("toasts.uploadRetry"),
          description: t("toasts.uploadRetryDesc"),
          variant: "destructive",
        });
      } finally {
        setAssetUploading(null);
      }
    },
    [
      uploadSeedanceAsset,
      isMotion,
      isSeedance,
      isVeo,
      selectedModel,
      motionCharacterImage,
      toast,
      t,
    ]
  );

  const handleGenerate = useCallback(async () => {
    if (isHeygen) {
      if (!heygenAvatarId) {
        toast({
          title: t("toasts.heygenSelectAvatar"),
          description: t("toasts.heygenSelectAvatarDesc"),
          variant: "destructive",
        });
        return;
      }
      if (!heygenVoiceId) {
        toast({
          title: t("toasts.heygenSelectVoice"),
          description: t("toasts.heygenSelectVoiceDesc"),
          variant: "destructive",
        });
        return;
      }
      if (!heygenScript.trim()) {
        toast({
          title: t("toasts.heygenEnterScript"),
          description: t("toasts.heygenEnterScriptDesc"),
          variant: "destructive",
        });
        return;
      }

      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setGenerationState({ status: "queued", message: t("toasts.heygenQueued") });
      const token = getAuthToken();
      const heygenAspect: HeyGenAspect =
        aspectRatio === "16:9" || aspectRatio === "9:16" || aspectRatio === "1:1"
          ? aspectRatio
          : "9:16";

      try {
        const resp = await fetch("/api/ad-creator/heygen/generate", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token ?? ""}` },
          body: JSON.stringify({
            avatarId: heygenAvatarId,
            voiceId: heygenVoiceId,
            script: heygenScript.trim(),
            resolution: heygenResolution,
            aspectRatio: heygenAspect,
            avatarStyle: heygenAvatarStyle,
            expressiveness: heygenExpressiveness,
            caption: heygenCaption,
          }),
          signal: ctrl.signal,
        });

        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(data?.message || `HTTP ${resp.status}`);

        const videoId: string = data.videoId;
        setGenerationState({ status: "in_progress", message: t("toasts.heygenRendering") });

        const POLL_MS = 5000;
        const MAX_TRIES = 180; // ~15 min
        for (let attempt = 0; attempt < MAX_TRIES; attempt++) {
          if (ctrl.signal.aborted) return;
          await new Promise((r) => setTimeout(r, POLL_MS));
          const statusResp = await fetch(`/api/ad-creator/heygen/status/${videoId}`, {
            headers: { Authorization: `Bearer ${token ?? ""}` },
            signal: ctrl.signal,
          });
          const statusData = await statusResp.json().catch(() => ({}));
          if (!statusResp.ok) throw new Error(statusData?.message || `HTTP ${statusResp.status}`);

          if (statusData.status === "completed" && statusData.video_url) {
            const generated: GeneratedVideo = {
              id: videoId,
              videoUrl: statusData.video_url,
              prompt: heygenScript.trim().slice(0, 200),
              model: "heygen-avatar",
              durationSeconds: Math.round(statusData.duration ?? 0),
              aspectRatio: heygenAspect,
              fileSizeBytes: 0,
            };
            setGeneratedVideos((prev) => [generated, ...prev]);
            setGenerationState({ status: "completed" });
            toast({ title: t("toasts.heygenDone"), description: t("toasts.heygenDoneDesc") });
            return;
          }
          if (statusData.status === "failed") {
            throw new Error(statusData.failure_message || t("toasts.heygenError"));
          }
          setGenerationState({ status: "in_progress", message: `HeyGen: ${statusData.status}…` });
        }
        throw new Error(t("toasts.heygenTimeout"));
      } catch (err: unknown) {
        if ((err as Error).name === "AbortError") return;
        const msg = (err as Error).message || t("toasts.heygenUnknownError");
        setGenerationState({ status: "error", message: msg });
        toast({ title: t("toasts.heygenError"), description: msg, variant: "destructive" });
      }
      return;
    }

    if (!prompt.trim() && !isMotion) {
      toast({
        title: t("toasts.noPrompt"),
        description: t("toasts.noPromptDesc"),
        variant: "destructive",
      });
      return;
    }

    if (isMotion) {
      if (!motionCharacterImage) {
        toast({
          title: t("toasts.noCharacter"),
          description: t("toasts.noCharacterDesc"),
          variant: "destructive",
        });
        return;
      }
      if (!motionDrivingVideo) {
        toast({
          title: t("toasts.noDrivingVideo"),
          description: t("toasts.noDrivingVideoDesc"),
          variant: "destructive",
        });
        return;
      }
      if (!motionConsent) {
        toast({
          title: t("toasts.noConsent"),
          description: t("toasts.noConsentDesc"),
          variant: "destructive",
        });
        return;
      }
      if (motionOrientation === "image" && motionDrivingVideo.durationSeconds > 10.5) {
        toast({
          title: t("toasts.tooLong"),
          description: t("toasts.tooLongMotionDesc", {
            duration: motionDrivingVideo.durationSeconds.toFixed(1),
          }),
          variant: "destructive",
        });
        return;
      }
    }

    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setGenerationState({ status: "queued", message: t("toasts.queuingJob") });

    const token = getAuthToken();
    const body: Record<string, unknown> = {
      prompt: prompt.trim() || (isMotion ? "motion control transfer" : ""),
      model: selectedModel,
      aspectRatio,
      duration,
    };
    if (negativePrompt.trim() && selectedModel !== "seedance-2.0")
      body.negativePrompt = negativePrompt.trim();
    if (referenceImageBase64 && (selectedModel === "kling-2.1" || isVeo))
      body.referenceImageUrl = referenceImageBase64;
    if (isMotion) {
      body.referenceImageUrl = motionCharacterImage!.url;
      body.drivingVideoUrl = motionDrivingVideo!.url;
      body.characterOrientation = motionOrientation;
      body.motionConsentConfirmed = true;
    }
    if (isEdit) {
      if (!editVideoUrl) {
        setGenerationState({ status: "idle" });
        toast({
          title: "Brak wideo źródłowego",
          description: "Wgraj wideo (3–10s) aby je edytować.",
          variant: "destructive",
        });
        return;
      }
      body.videoToEditUrl = editVideoUrl;
      if (seedanceImages.length > 0)
        body.referenceImageUrls = seedanceImages.slice(0, 4).map((i) => i.url);
    }
    if (selectedModel === "seedance-2.0") {
      body.resolution = resolution;
      body.generateAudio = generateAudio;
      body.seedanceMode = seedanceMode;
      if (seedanceImages.length > 0) body.referenceImageUrls = seedanceImages.map((i) => i.url);
      if (seedanceVideos.length > 0) body.videoUrls = seedanceVideos.map((v) => v.url);
      if (seedanceAudios.length > 0) {
        const hasVisualRef = seedanceImages.length > 0 || seedanceVideos.length > 0;
        if (!hasVisualRef) {
          setGenerationState({ status: "idle" });
          toast({
            title: t("toasts.noAudioRef"),
            description: t("toasts.noAudioRefDesc"),
            variant: "destructive",
          });
          return;
        }
        body.audioUrls = seedanceAudios.map((a) => a.url);
      }
    }

    try {
      const resp = await fetch("/api/ad-creator/generate-video", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token ?? ""}`,
        },
        body: JSON.stringify(body),
        signal: ctrl.signal,
      });

      if (!resp.ok || !resp.body) {
        const err = await resp.json().catch(() => ({ message: t("toasts.connectionError") }));
        throw new Error(err.message || `HTTP ${resp.status}`);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        for (const line of buffer.split("\n")) {
          if (!line.startsWith("data: ")) continue;
          buffer = "";
          const raw = line.slice(6).trim();
          if (!raw) continue;

          const event = JSON.parse(raw) as Record<string, unknown>;

          if (event.type === "progress") {
            setGenerationState({
              status: (event.status as GenerationStatus) ?? "in_progress",
              message: event.message as string | undefined,
              queuePosition: event.queuePosition as number | undefined,
            });
          } else if (event.type === "done") {
            const video = event.video as GeneratedVideo;
            setGeneratedVideos((prev) => [video, ...prev]);
            setGenerationState({ status: "completed" });
            qc.invalidateQueries({ queryKey: ["graphics-library"] });
            qc.invalidateQueries({ queryKey: ["graphics-quota"] });
            qc.invalidateQueries({ queryKey: ["video-ad-history"] });
            const meta = event.meta as
              | { tokensDeducted: number; tokensRemaining: number }
              | undefined;
            toast({
              title: t("toasts.videoGenerated"),
              description: meta
                ? t("toasts.videoGeneratedWithMeta", {
                    deducted: meta.tokensDeducted,
                    remaining: meta.tokensRemaining,
                  })
                : t("toasts.videoReady"),
            });
          } else if (event.type === "error") {
            throw new Error((event.message as string) || t("toasts.generationError"));
          }
        }
      }
    } catch (err: unknown) {
      if ((err as Error).name === "AbortError") return;
      const msg = (err as Error).message || t("toasts.unknownError");
      setGenerationState({ status: "error", message: msg });
      toast({ title: t("toasts.retryGenerate"), description: msg, variant: "destructive" });
    }
  }, [
    prompt,
    selectedModel,
    aspectRatio,
    duration,
    negativePrompt,
    referenceImageBase64,
    resolution,
    generateAudio,
    seedanceImages,
    seedanceVideos,
    seedanceAudios,
    isMotion,
    motionCharacterImage,
    motionDrivingVideo,
    motionConsent,
    motionOrientation,
    isHeygen,
    heygenAvatarId,
    heygenVoiceId,
    heygenScript,
    heygenResolution,
    toast,
    qc,
  ]);

  const handleCancel = useCallback(() => {
    abortRef.current?.abort();
    setGenerationState({ status: "idle" });
  }, []);

  const handleDeleteVideo = useCallback((id: string) => {
    setGeneratedVideos((prev) => prev.filter((v) => v.id !== id));
  }, []);

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold text-white">{t("header.title")}</h2>
        <p className="text-xs text-white/40 mt-0.5">{t("header.subtitle")}</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left: Form sidebar */}
        <div className="space-y-4 w-full lg:w-[400px] lg:shrink-0">
          {/* Top tabs */}
          <div className="flex items-center gap-5 border-b border-white/[0.06] pb-2">
            <button
              onClick={() => {
                if (currentModel.category !== "create") {
                  const fallback = VIDEO_MODELS.find((m) => m.category === "create");
                  if (fallback) handleModelChange(fallback.id);
                }
                setTopTab("create");
              }}
              className={`text-sm pb-1.5 transition ${
                topTab === "create"
                  ? "text-white font-medium border-b-2 border-red-500"
                  : "text-white/30 hover:text-white/60"
              }`}
            >
              Stwórz wideo
            </button>
            <button
              onClick={() => {
                if (currentModel.category !== "edit") {
                  const fallback = VIDEO_MODELS.find((m) => m.category === "edit");
                  if (fallback) handleModelChange(fallback.id);
                }
                setTopTab("edit");
              }}
              className={`text-sm pb-1.5 transition ${
                topTab === "edit"
                  ? "text-white font-medium border-b-2 border-red-500"
                  : "text-white/30 hover:text-white/60"
              }`}
            >
              Edytuj
            </button>
            <button
              onClick={() => {
                if (currentModel.category !== "motion") {
                  const fallback = VIDEO_MODELS.find((m) => m.category === "motion");
                  if (fallback) handleModelChange(fallback.id);
                }
                setTopTab("motion");
              }}
              className={`text-sm pb-1.5 transition ${
                topTab === "motion"
                  ? "text-white font-medium border-b-2 border-red-500"
                  : "text-white/30 hover:text-white/60"
              }`}
            >
              Motion Control
            </button>
          </div>

          {/* Edit Video upload zones */}
          {isEdit && (
            <>
              <input
                ref={editVideoInputRef}
                type="file"
                accept="video/mp4,video/quicktime"
                className="hidden"
                onChange={handleEditVideoUpload}
              />
              <button
                onClick={() => editVideoInputRef.current?.click()}
                className="w-full rounded-xl bg-white/[0.03] border border-dashed border-white/[0.12] hover:border-white/[0.2] hover:bg-white/[0.05] transition flex flex-col items-center justify-center gap-2 py-6 text-center"
              >
                <span className="w-10 h-10 rounded-full bg-white/[0.06] border border-white/[0.06] flex items-center justify-center">
                  {assetUploading === "video" ? (
                    <Loader2 className="w-4 h-4 text-white/70 animate-spin" />
                  ) : (
                    <Film className="w-4 h-4 text-white/70" />
                  )}
                </span>
                <div>
                  {editVideoUrl ? (
                    <>
                      <div className="text-sm text-white/80 truncate max-w-[280px]">
                        {editVideoName}
                      </div>
                      <div className="text-[11px] text-white/40 mt-0.5">Kliknij aby zmienić</div>
                    </>
                  ) : (
                    <>
                      <div className="text-sm text-white/80">Wgraj wideo do edycji</div>
                      <div className="text-[11px] text-white/30 mt-0.5">
                        Wymagana długość: 3–10s
                      </div>
                    </>
                  )}
                </div>
              </button>
              <input
                ref={seedanceImagesInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                className="hidden"
                onChange={handleSeedanceImagesUpload}
              />
              <button
                onClick={() => seedanceImagesInputRef.current?.click()}
                disabled={seedanceImages.length >= 4}
                className="w-full rounded-xl bg-white/[0.03] border border-dashed border-white/[0.12] hover:border-white/[0.2] hover:bg-white/[0.05] transition flex flex-col items-center justify-center gap-2 py-6 text-center disabled:opacity-50 disabled:cursor-not-allowed relative"
              >
                <span className="absolute top-2 right-2 text-[10px] text-white/30">Opcjonalne</span>
                <span className="w-10 h-10 rounded-full bg-white/[0.06] border border-white/[0.06] flex items-center justify-center">
                  {assetUploading === "image" ? (
                    <Loader2 className="w-4 h-4 text-white/70 animate-spin" />
                  ) : (
                    <ImageIcon className="w-4 h-4 text-white/70" />
                  )}
                </span>
                <div>
                  <div className="text-sm text-white/80">Wgraj obrazy & elementy</div>
                  <div className="text-[11px] text-white/30 mt-0.5">
                    Do 4 obrazów ({seedanceImages.length}/4)
                  </div>
                </div>
              </button>
            </>
          )}

          {/* Upload media — unified zone (hidden for HeyGen and Edit) */}
          {!isHeygen &&
            !isEdit &&
            (() => {
              const acceptsImage = isMotion || isSeedance || selectedModel === "kling-2.1" || isVeo;
              const acceptsVideo = isMotion || isSeedance;
              const acceptsAudio = isSeedance;
              const acceptParts: string[] = [];
              if (acceptsImage) acceptParts.push("image/jpeg,image/png,image/webp");
              if (acceptsVideo) acceptParts.push("video/mp4,video/quicktime");
              if (acceptsAudio) acceptParts.push("audio/mpeg,audio/mp3,audio/wav,audio/x-wav");
              const labelParts: string[] = [];
              if (acceptsImage) labelParts.push("zdjęcie");
              if (acceptsVideo) labelParts.push("wideo");
              if (acceptsAudio) labelParts.push("audio");
              const labelText =
                labelParts.length === 1
                  ? `Wgraj ${labelParts[0]}`
                  : `Wgraj ${labelParts.slice(0, -1).join(", ")} lub ${labelParts.slice(-1)}`;
              return (
                <>
                  <input
                    ref={unifiedMediaInputRef}
                    type="file"
                    accept={acceptParts.join(",")}
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      const files = Array.from(e.target.files ?? []);
                      e.target.value = "";
                      if (!files.length) return;
                      const images = files.filter((f) => f.type.startsWith("image/"));
                      const videos = files.filter((f) => f.type.startsWith("video/"));
                      const audios = files.filter((f) => f.type.startsWith("audio/"));

                      const trigger = (ref: React.RefObject<HTMLInputElement>, list: File[]) => {
                        if (!ref.current || !list.length) return;
                        const dt = new DataTransfer();
                        list.forEach((f) => dt.items.add(f));
                        ref.current.files = dt.files;
                        ref.current.dispatchEvent(new Event("change", { bubbles: true }));
                      };

                      if (isMotion) {
                        trigger(motionCharacterInputRef, images.slice(0, 1));
                        trigger(motionDrivingInputRef, videos.slice(0, 1));
                      } else if (isSeedance) {
                        trigger(seedanceImagesInputRef, images);
                        trigger(seedanceVideosInputRef, videos);
                        trigger(seedanceAudiosInputRef, audios);
                      } else if (selectedModel === "kling-2.1" || isVeo) {
                        trigger(fileInputRef, images.slice(0, 1));
                      }
                    }}
                  />
                  <button
                    onClick={() => unifiedMediaInputRef.current?.click()}
                    className="w-full rounded-xl bg-white/[0.03] border border-dashed border-white/[0.12] hover:border-white/[0.2] hover:bg-white/[0.05] transition flex flex-col items-center justify-center gap-2 py-6 text-center"
                  >
                    <div className="flex items-center -space-x-2">
                      {acceptsImage && (
                        <span className="w-9 h-9 rounded-full bg-white/[0.06] border border-white/[0.06] flex items-center justify-center">
                          <ImageIcon className="w-4 h-4 text-white/70" />
                        </span>
                      )}
                      {acceptsVideo && (
                        <span className="w-9 h-9 rounded-full bg-white/[0.06] border border-white/[0.06] flex items-center justify-center">
                          <Film className="w-4 h-4 text-white/70" />
                        </span>
                      )}
                      {acceptsAudio && (
                        <span className="w-9 h-9 rounded-full bg-white/[0.06] border border-white/[0.06] flex items-center justify-center">
                          <Music className="w-4 h-4 text-white/70" />
                        </span>
                      )}
                    </div>
                    <div>
                      <div className="text-sm text-white/80">{labelText}</div>
                      <div className="text-[11px] text-white/30 mt-0.5">
                        {isMotion ? "Postać i wideo sterujące" : "Pliki referencyjne"}
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setLibraryPickerOpen(true)}
                    className="w-full -mt-1 flex items-center justify-center gap-1.5 py-1.5 text-[11px] text-white/50 hover:text-white/80 transition"
                  >
                    <FolderOpen className="w-3 h-3" /> lub wybierz z biblioteki
                  </button>
                </>
              );
            })()}

          {/* Prompt card */}
          {!isHeygen && (
            <div className="rounded-xl bg-white/[0.03] border border-white/[0.08] p-3">
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <div className="text-[11px] text-white/50 shrink-0">Prompt</div>
                <Select onValueChange={setPrompt}>
                  <SelectTrigger className="h-6 w-auto max-w-[180px] text-[10px] bg-transparent border-white/[0.06] text-white/40 gap-1 px-2 truncate">
                    <SelectValue placeholder={t("prompt.examplesPlaceholder")} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0d0f14] border-white/[0.08] max-w-xs">
                    {PROMPT_EXAMPLES.map((ex, i) => (
                      <SelectItem
                        key={i}
                        value={ex}
                        className="text-[10px] text-white/70 whitespace-normal"
                      >
                        {ex.slice(0, 60)}…
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onInput={(e) => {
                  const ta = e.currentTarget;
                  ta.style.height = "auto";
                  ta.style.height = Math.min(ta.scrollHeight, 360) + "px";
                }}
                placeholder={
                  isMotion
                    ? t("prompt.placeholderMotion")
                    : isEdit
                      ? 'Opisz zmianę, np. "Niech pada śnieg" lub "Zmień na noc". Użyj @ aby dodać element.'
                      : t("prompt.placeholder")
                }
                rows={4}
                className="w-full bg-transparent text-sm text-white placeholder:text-white/25 focus:outline-none resize-none overflow-y-auto max-h-[360px]"
              />
              <div className="flex items-center gap-2 mt-2">
                <button
                  type="button"
                  className="flex items-center gap-1 px-2 py-1 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] transition text-[11px] text-white/60"
                  title="Wstaw odniesienie do zasobu"
                  onClick={() => setPrompt((p) => p + " @")}
                >
                  <AtSign className="w-3 h-3" /> Elementy
                </button>
                {isSeedance && (
                  <button
                    type="button"
                    onClick={() => setGenerateAudio((v) => !v)}
                    className={`flex items-center gap-1 px-2 py-1 rounded-lg transition text-[11px] ${
                      generateAudio
                        ? "bg-red-500/20 text-red-300"
                        : "bg-white/[0.04] text-white/60 hover:bg-white/[0.08]"
                    }`}
                    title={t("generateAudio.label")}
                  >
                    {generateAudio ? (
                      <Volume2 className="w-3 h-3" />
                    ) : (
                      <VolumeX className="w-3 h-3" />
                    )}
                    Audio: {generateAudio ? "Wł" : "Wył"}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Model selector — single card opens dialog */}
          <button
            onClick={() => setModelDialogOpen(true)}
            className="w-full rounded-xl bg-white/[0.03] border border-white/[0.08] hover:border-white/[0.15] transition px-3.5 py-3 flex items-center justify-between text-left"
          >
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-white/40">Model</div>
              <div className="text-sm text-white font-medium truncate flex items-center gap-1.5">
                {currentModel.label}
                {currentModel.badge && (
                  <span className="text-[9px] text-white/40 truncate">
                    ·{" "}
                    {currentModel.badge.startsWith("models.")
                      ? t(currentModel.badge)
                      : currentModel.badge}
                  </span>
                )}
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-white/30 shrink-0" />
          </button>

          {/* Auto settings toggle (Edit mode) */}
          {isEdit && (
            <div className="flex items-center justify-between rounded-xl bg-white/[0.03] border border-white/[0.08] px-3.5 py-3">
              <div className="text-sm text-white/80">Ustawienia automatyczne</div>
              <button
                onClick={() => setAutoSettings((v) => !v)}
                className={`relative w-10 h-5 rounded-full transition-colors ${autoSettings ? "bg-red-500" : "bg-white/10"}`}
                aria-label="toggle auto settings"
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${autoSettings ? "translate-x-5" : ""}`}
                />
              </button>
            </div>
          )}

          {/* Compact 3-chip row: duration / aspect / quality (hidden for Edit) */}
          {!isEdit && (
            <div className={`grid ${isSeedance ? "grid-cols-4" : "grid-cols-3"} gap-2`}>
              {/* Duration chip */}
              {!isHeygen && !isMotion ? (
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="rounded-xl bg-white/[0.03] border border-white/[0.08] hover:border-white/[0.15] transition px-2 py-2.5 flex items-center justify-center gap-1.5 text-xs text-white/80">
                      <Clock className="w-3 h-3" /> {duration}s
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-56 bg-[#0d0f14] border-white/[0.08] p-3"
                    align="start"
                  >
                    <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">
                      {t("duration.label")}
                    </div>
                    {isSeedance ? (
                      <div className="space-y-1.5">
                        <input
                          type="range"
                          min={currentModel.minDuration}
                          max={currentModel.maxDuration}
                          step={1}
                          value={duration}
                          onChange={(e) => setDuration(parseInt(e.target.value, 10))}
                          className="w-full accent-red-500"
                        />
                        <div className="flex justify-between text-[10px] text-white/30">
                          <span>{currentModel.minDuration}s</span>
                          <span className="text-white/70">{duration}s</span>
                          <span>{currentModel.maxDuration}s</span>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 gap-1.5">
                        {(isVeo ? [4, 6, 8] : ([5, 10] as VideoDuration[])).map((d) => {
                          const disabled =
                            d > currentModel.maxDuration || d < currentModel.minDuration;
                          return (
                            <button
                              key={d}
                              onClick={() => !disabled && setDuration(d)}
                              disabled={disabled}
                              className={`px-2 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                                duration === d && !disabled
                                  ? "border-red-500/50 bg-red-500/10 text-white"
                                  : disabled
                                    ? "border-white/[0.04] text-white/20 cursor-not-allowed"
                                    : "border-white/[0.08] bg-white/[0.03] text-white/50 hover:border-white/20"
                              }`}
                            >
                              {d}s
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
              ) : (
                <div className="rounded-xl bg-white/[0.02] border border-white/[0.06] px-2 py-2.5 flex items-center justify-center gap-1.5 text-xs text-white/40">
                  <Clock className="w-3 h-3" /> {isMotion ? "Auto" : "—"}
                </div>
              )}

              {/* Aspect ratio chip */}
              <Popover>
                <PopoverTrigger asChild>
                  <button className="rounded-xl bg-white/[0.03] border border-white/[0.08] hover:border-white/[0.15] transition px-2 py-2.5 flex items-center justify-center gap-1.5 text-xs text-white/80">
                    <Smartphone className="w-3 h-3" />{" "}
                    {aspectRatio === "auto" ? "Auto" : aspectRatio}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-56 bg-[#0d0f14] border-white/[0.08] p-3" align="start">
                  <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">
                    {t("aspectRatio.label")}
                  </div>
                  <div className="grid grid-cols-3 gap-1.5">
                    {availableAspectRatios.map((r) => (
                      <button
                        key={r.id}
                        onClick={() => setAspectRatio(r.id)}
                        className={`px-2 py-2 rounded-lg border text-xs transition-all ${
                          aspectRatio === r.id
                            ? "border-red-500/50 bg-red-500/10 text-white"
                            : "border-white/[0.08] bg-white/[0.03] text-white/50 hover:border-white/20"
                        }`}
                      >
                        <div className="text-base">{r.icon}</div>
                        <div className="font-medium">{r.label}</div>
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

              {/* Quality chip */}
              {isSeedance ? (
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="rounded-xl bg-white/[0.03] border border-white/[0.08] hover:border-white/[0.15] transition px-2 py-2.5 flex items-center justify-center gap-1.5 text-xs text-white/80">
                      <Gem className="w-3 h-3" /> {resolution}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-56 bg-[#0d0f14] border-white/[0.08] p-3"
                    align="start"
                  >
                    <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2">
                      {t("resolution.label")}
                    </div>
                    <div className="grid grid-cols-3 gap-1.5">
                      {RESOLUTIONS.map((r) => (
                        <button
                          key={r.id}
                          onClick={() => setResolution(r.id)}
                          className={`px-2 py-2 rounded-lg border text-xs transition-all ${
                            resolution === r.id
                              ? "border-red-500/50 bg-red-500/10 text-white"
                              : "border-white/[0.08] bg-white/[0.03] text-white/50 hover:border-white/20"
                          }`}
                        >
                          <div className="font-medium">{r.label}</div>
                        </button>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              ) : (
                <div className="rounded-xl bg-white/[0.02] border border-white/[0.06] px-2 py-2.5 flex items-center justify-center gap-1.5 text-xs text-white/40">
                  <Gem className="w-3 h-3" /> {isVeo ? "1080p" : "Auto"}
                </div>
              )}
              {isSeedance && (
                <button
                  type="button"
                  onClick={() => setGenerateAudio((v) => !v)}
                  className={`rounded-xl border transition px-2 py-2.5 flex items-center justify-center gap-1.5 text-xs ${
                    generateAudio
                      ? "bg-red-500/10 border-red-500/40 text-red-200"
                      : "bg-white/[0.03] border-white/[0.08] text-white/60 hover:border-white/[0.15]"
                  }`}
                  title={t("generateAudio.label")}
                >
                  {generateAudio ? (
                    <Volume2 className="w-3 h-3" />
                  ) : (
                    <VolumeX className="w-3 h-3" />
                  )}
                  {generateAudio ? "Audio Wł" : "Audio Wył"}
                </button>
              )}
            </div>
          )}

          <p className="text-[10px] text-white/30">{t(currentModel.desc)}</p>

          {/* Seedance mode toggle: image-to-video vs reference-to-video */}
          {isSeedance &&
            (() => {
              const imgCount = seedanceImages.length;
              const hasOther = seedanceVideos.length > 0 || seedanceAudios.length > 0;
              const canI2V = imgCount === 1 && !hasOther;
              const effective =
                seedanceMode === "auto" ? (canI2V ? "i2v" : "reference") : seedanceMode;
              return (
                <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-2.5 space-y-2">
                  <div className="text-[10px] uppercase tracking-wider text-white/40">
                    Tryb Seedance
                  </div>
                  <div className="grid grid-cols-3 gap-1.5">
                    <button
                      type="button"
                      onClick={() => setSeedanceMode("auto")}
                      className={`px-2 py-2 rounded-lg border text-[11px] font-medium transition ${
                        seedanceMode === "auto"
                          ? "border-red-500/50 bg-red-500/10 text-white"
                          : "border-white/[0.08] bg-white/[0.02] text-white/60 hover:border-white/[0.15]"
                      }`}
                      title="Auto: i2v gdy 1 zdjęcie, inaczej reference"
                    >
                      Auto
                    </button>
                    <button
                      type="button"
                      onClick={() => setSeedanceMode("i2v")}
                      disabled={!canI2V}
                      className={`px-2 py-2 rounded-lg border text-[11px] font-medium transition ${
                        seedanceMode === "i2v"
                          ? "border-red-500/50 bg-red-500/10 text-white"
                          : !canI2V
                            ? "border-white/[0.04] bg-white/[0.02] text-white/20 cursor-not-allowed"
                            : "border-white/[0.08] bg-white/[0.02] text-white/60 hover:border-white/[0.15]"
                      }`}
                      title="Image-to-Video: pierwsza klatka 1:1 (wymaga dokładnie 1 zdjęcia, bez wideo/audio)"
                    >
                      Image→Video
                    </button>
                    <button
                      type="button"
                      onClick={() => setSeedanceMode("reference")}
                      className={`px-2 py-2 rounded-lg border text-[11px] font-medium transition ${
                        seedanceMode === "reference"
                          ? "border-red-500/50 bg-red-500/10 text-white"
                          : "border-white/[0.08] bg-white/[0.02] text-white/60 hover:border-white/[0.15]"
                      }`}
                      title="Reference: do 9 obrazów + 3 wideo + 3 audio jako inspiracja"
                    >
                      Reference
                    </button>
                  </div>
                  <p className="text-[10px] text-white/40 leading-relaxed">
                    {effective === "i2v"
                      ? "✓ Pierwsza klatka będzie 1:1 z Twojego zdjęcia."
                      : "⚠ Zdjęcia są tylko inspiracją — wideo nie zaczyna się dokładnie od nich."}
                  </p>
                  {seedanceMode === "i2v" && !canI2V && (
                    <p className="text-[10px] text-amber-400/80">
                      Image→Video wymaga dokładnie 1 zdjęcia i braku wideo/audio. Tryb wróci do
                      Reference.
                    </p>
                  )}
                </div>
              );
            })()}

          {/* Motion duration info */}
          {isMotion && (
            <div className="px-3 py-2 rounded-lg border border-white/[0.08] bg-white/[0.02] text-[11px] text-white/50 leading-relaxed">
              {t("duration.motionInfo")}
              <br />
              <span className="text-amber-400/80">{t("duration.motionLimit")}</span>
              {motionDrivingVideo ? (
                <div className="mt-2 flex items-center justify-between gap-2 px-2 py-1.5 rounded-md bg-white/[0.04]">
                  <span className="text-white/70">{t("duration.recordingLabel")}</span>
                  <span className="text-white font-medium">
                    {motionDrivingVideo.durationSeconds.toFixed(1)}s
                  </span>
                </div>
              ) : (
                <div className="mt-2 text-white/30 italic">{t("duration.noDrivingVideo")}</div>
              )}
            </div>
          )}

          {/* (legacy block removed — audio toggle now lives in Prompt chips) */}
          {false && (
            <div className="flex items-center justify-between rounded-lg border border-white/[0.08] bg-white/[0.03] px-3 py-2">
              <div>
                <Label className="text-xs text-white/80">{t("generateAudio.label")}</Label>
                <p className="text-[10px] text-white/40 mt-0.5">{t("generateAudio.desc")}</p>
              </div>
              <button
                onClick={() => setGenerateAudio((v) => !v)}
                className={`relative w-10 h-5 rounded-full transition-colors ${generateAudio ? "bg-red-500" : "bg-white/10"}`}
                aria-label="toggle audio"
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${generateAudio ? "translate-x-5" : ""}`}
                />
              </button>
            </div>
          )}

          {/* Hidden file inputs — triggered by unified Wgraj media zone */}
          {isSeedance && (
            <>
              <input
                ref={seedanceImagesInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                className="hidden"
                onChange={handleSeedanceImagesUpload}
              />
              <input
                ref={seedanceVideosInputRef}
                type="file"
                accept="video/mp4,video/quicktime"
                multiple
                className="hidden"
                onChange={handleSeedanceVideosUpload}
              />
              <input
                ref={seedanceAudiosInputRef}
                type="file"
                accept="audio/mpeg,audio/mp3,audio/wav,audio/x-wav"
                multiple
                className="hidden"
                onChange={handleSeedanceAudiosUpload}
              />
            </>
          )}
          {(selectedModel === "kling-2.1" || isVeo) && (
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={handleReferenceImageUpload}
            />
          )}

          {/* Uploaded media previews — single compact block */}
          {isSeedance &&
            (seedanceImages.length > 0 ||
              seedanceVideos.length > 0 ||
              seedanceAudios.length > 0) && (
              <div className="space-y-2">
                {seedanceImages.length > 0 && (
                  <div className="grid grid-cols-4 gap-2">
                    {seedanceImages.map((img, idx) => (
                      <div key={idx} className="relative group">
                        <img
                          src={img.previewUrl}
                          alt={img.name}
                          className="w-full aspect-square object-cover rounded-md border border-white/[0.08]"
                        />
                        <div className="absolute inset-x-0 bottom-0 px-1 py-0.5 bg-black/70 text-[9px] text-white/70 rounded-b-md truncate">
                          @Image{idx + 1}
                        </div>
                        <button
                          onClick={() => removeSeedanceImage(idx)}
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-black/80 border border-white/20 flex items-center justify-center text-white/70 hover:text-red-400"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {seedanceVideos.map((v, idx) => (
                  <div
                    key={`v${idx}`}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-md border border-white/[0.08] bg-white/[0.03] text-[10px] text-white/60"
                  >
                    <Film className="w-3 h-3 text-white/40 shrink-0" />
                    <span className="truncate flex-1" title={v.name}>
                      @Video{idx + 1}: {v.name}
                    </span>
                    <button
                      onClick={() => setSeedanceVideos((prev) => prev.filter((_, i) => i !== idx))}
                      className="text-white/30 hover:text-red-400"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                {seedanceAudios.map((a, idx) => (
                  <div
                    key={`a${idx}`}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-md border border-white/[0.08] bg-white/[0.03] text-[10px] text-white/60"
                  >
                    <span className="text-white/40">♪</span>
                    <span className="truncate flex-1" title={a.name}>
                      @Audio{idx + 1}: {a.name}
                    </span>
                    <button
                      onClick={() => setSeedanceAudios((prev) => prev.filter((_, i) => i !== idx))}
                      className="text-white/30 hover:text-red-400"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

          {(selectedModel === "kling-2.1" || isVeo) && referenceImageBase64 && (
            <div className="flex items-center gap-2 p-2 rounded-lg border border-white/[0.08] bg-white/[0.03]">
              <div className="w-10 h-10 rounded-md overflow-hidden bg-white/[0.04] border border-white/[0.06] shrink-0">
                <img
                  src={referenceImageBase64}
                  alt={referenceImageName}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              </div>
              <span className="text-xs text-white/60 truncate flex-1">{referenceImageName}</span>
              <button
                onClick={() => {
                  setReferenceImageBase64(undefined);
                  setReferenceImageName("");
                }}
                className="p-0.5 rounded text-white/30 hover:text-red-400 transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {/* Motion Control: character image + driving video */}
          {isMotion && (
            <div className="space-y-3 rounded-xl border border-red-500/20 bg-red-500/[0.03] p-3">
              <div>
                <div className="text-xs font-medium text-red-300">{t("motionControl.title")}</div>
                <p className="text-[10px] text-white/40 mt-0.5">{t("motionControl.description")}</p>
              </div>

              {/* Character image */}
              <div className="space-y-1.5">
                <Label className="text-xs text-white/60">
                  {t("motionControl.character.label")}{" "}
                  <span className="text-white/30">{t("motionControl.character.hint")}</span>
                </Label>
                <input
                  ref={motionCharacterInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={handleMotionCharacterUpload}
                />
                {motionCharacterImage ? (
                  <div className="flex items-center gap-2 p-2 rounded-lg border border-white/[0.08] bg-white/[0.03]">
                    <img
                      src={motionCharacterImage.previewUrl}
                      alt=""
                      className="w-8 h-8 object-cover rounded"
                    />
                    <span className="text-xs text-white/60 truncate flex-1">
                      {motionCharacterImage.name}
                    </span>
                    <button
                      onClick={() => {
                        if (motionCharacterImage.previewUrl)
                          URL.revokeObjectURL(motionCharacterImage.previewUrl);
                        setMotionCharacterImage(null);
                      }}
                      className="p-0.5 rounded text-white/30 hover:text-red-400 transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => motionCharacterInputRef.current?.click()}
                    disabled={motionUploading === "character"}
                    className="flex items-center gap-2 w-full px-3 py-2 rounded-lg border border-dashed border-white/[0.12] text-xs text-white/40 hover:text-white/60 hover:border-white/20 transition-colors disabled:opacity-50"
                  >
                    {motionUploading === "character" ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Upload className="w-3.5 h-3.5" />
                    )}
                    {motionUploading === "character"
                      ? t("motionControl.character.uploading")
                      : t("motionControl.character.addButton")}
                  </button>
                )}
              </div>

              {/* Driving video */}
              <div className="space-y-1.5">
                <Label className="text-xs text-white/60">
                  {t("motionControl.driving.label")}{" "}
                  <span className="text-white/30">{t("motionControl.driving.hint")}</span>
                </Label>
                <input
                  ref={motionDrivingInputRef}
                  type="file"
                  accept="video/mp4,video/quicktime"
                  className="hidden"
                  onChange={handleMotionDrivingUpload}
                />
                {motionDrivingVideo ? (
                  <div className="flex items-center gap-2 p-2 rounded-lg border border-white/[0.08] bg-white/[0.03]">
                    <Film className="w-4 h-4 text-white/40 shrink-0" />
                    <span className="text-xs text-white/60 truncate flex-1">
                      {motionDrivingVideo.name}
                    </span>
                    <button
                      onClick={() => setMotionDrivingVideo(null)}
                      className="p-0.5 rounded text-white/30 hover:text-red-400 transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => motionDrivingInputRef.current?.click()}
                    disabled={motionUploading === "driving"}
                    className="flex items-center gap-2 w-full px-3 py-2 rounded-lg border border-dashed border-white/[0.12] text-xs text-white/40 hover:text-white/60 hover:border-white/20 transition-colors disabled:opacity-50"
                  >
                    {motionUploading === "driving" ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Upload className="w-3.5 h-3.5" />
                    )}
                    {motionUploading === "driving"
                      ? t("motionControl.driving.uploading")
                      : t("motionControl.driving.addButton")}
                  </button>
                )}
              </div>

              {/* Character orientation */}
              <div className="space-y-1.5">
                <Label className="text-xs text-white/60">
                  {t("motionControl.orientation.label")}{" "}
                  <span className="text-white/30">{t("motionControl.orientation.hint")}</span>
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    {
                      id: "image" as const,
                      label: t("motionControl.orientation.fromImage"),
                      icon: "🖼️",
                      desc: t("motionControl.orientation.fromImageDesc"),
                    },
                    {
                      id: "video" as const,
                      label: t("motionControl.orientation.fromVideo"),
                      icon: "🎬",
                      desc: t("motionControl.orientation.fromVideoDesc"),
                    },
                  ].map((opt) => (
                    <button
                      key={opt.id}
                      onClick={() => setMotionOrientation(opt.id)}
                      className={`px-2 py-2 rounded-lg border text-xs transition-all ${
                        motionOrientation === opt.id
                          ? "border-red-500/50 bg-red-500/10 text-white"
                          : "border-white/[0.08] bg-white/[0.03] text-white/50 hover:border-white/20"
                      }`}
                    >
                      <div className="text-base">{opt.icon}</div>
                      <div className="font-medium">{opt.label}</div>
                      <div className="text-[10px] text-white/40">{opt.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Consent checkbox */}
              <label className="flex items-start gap-2 text-[11px] text-white/60 cursor-pointer">
                <input
                  type="checkbox"
                  checked={motionConsent}
                  onChange={(e) => setMotionConsent(e.target.checked)}
                  className="mt-0.5 accent-red-500"
                />
                <span>{t("motionControl.consent")}</span>
              </label>
            </div>
          )}

          {/* HeyGen avatar mode */}
          {isHeygen && (
            <div className="space-y-3 rounded-xl border border-purple-500/20 bg-purple-500/[0.03] p-3">
              <div>
                <div className="text-xs font-medium text-purple-300">{t("heygen.title")}</div>
                <p className="text-[10px] text-white/40 mt-0.5">{t("heygen.description")}</p>
              </div>

              {/* Avatar picker — sub-tabs (Gotowe / Twoje / Utwórz własny) */}
              <HeyGenAvatarPicker selectedAvatarId={heygenAvatarId} onSelect={setHeygenAvatarId} />

              {/* Voice picker */}
              <div className="space-y-1.5">
                <Label className="text-xs text-white/60">{t("heygen.voice.label")}</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Select
                    value={heygenLanguageFilter}
                    onValueChange={(v) => {
                      setHeygenLanguageFilter(v);
                      setHeygenVoices([]);
                      setHeygenVoiceId("");
                    }}
                  >
                    <SelectTrigger className="h-8 text-xs bg-white/[0.04] border-white/[0.08] text-white">
                      <SelectValue placeholder={t("heygen.voice.language")} />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0d0f14] border-white/[0.08]">
                      <SelectItem value="polish" className="text-xs text-white/80">
                        Polski
                      </SelectItem>
                      <SelectItem value="english" className="text-xs text-white/80">
                        English
                      </SelectItem>
                      <SelectItem value="german" className="text-xs text-white/80">
                        Deutsch
                      </SelectItem>
                      <SelectItem value="spanish" className="text-xs text-white/80">
                        Español
                      </SelectItem>
                      <SelectItem value="french" className="text-xs text-white/80">
                        Français
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={heygenVoiceId} onValueChange={setHeygenVoiceId}>
                    <SelectTrigger className="h-8 text-xs bg-white/[0.04] border-white/[0.08] text-white">
                      <SelectValue
                        placeholder={
                          heygenLoading ? t("heygen.voice.loading") : t("heygen.voice.placeholder")
                        }
                      />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0d0f14] border-white/[0.08] max-h-64">
                      {heygenVoices.map((v) => (
                        <SelectItem
                          key={v.voice_id}
                          value={v.voice_id}
                          className="text-xs text-white/80"
                        >
                          {v.name} · {v.gender}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Resolution */}
              <div className="space-y-1.5">
                <Label className="text-xs text-white/60">{t("heygen.resolution.label")}</Label>
                <div className="grid grid-cols-3 gap-2">
                  {(["720p", "1080p", "4k"] as HeyGenResolution[]).map((r) => (
                    <button
                      key={r}
                      onClick={() => setHeygenResolution(r)}
                      className={`px-2 py-2 rounded-lg border text-xs transition-all ${
                        heygenResolution === r
                          ? "border-purple-500/50 bg-purple-500/10 text-white"
                          : "border-white/[0.08] bg-white/[0.03] text-white/50 hover:border-white/20"
                      }`}
                    >
                      <div className="font-medium">{r}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Avatar style */}
              <div className="space-y-1.5">
                <Label className="text-xs text-white/60">{t("heygen.avatarStyle.label")}</Label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    {
                      id: "normal" as HeyGenAvatarStyle,
                      label: t("heygen.avatarStyle.normal"),
                      desc: t("heygen.avatarStyle.normalDesc"),
                    },
                    {
                      id: "closeUp" as HeyGenAvatarStyle,
                      label: t("heygen.avatarStyle.closeUp"),
                      desc: t("heygen.avatarStyle.closeUpDesc"),
                    },
                    {
                      id: "circle" as HeyGenAvatarStyle,
                      label: t("heygen.avatarStyle.circle"),
                      desc: t("heygen.avatarStyle.circleDesc"),
                    },
                  ].map((s) => (
                    <button
                      key={s.id}
                      onClick={() => setHeygenAvatarStyle(s.id)}
                      className={`px-2 py-2 rounded-lg border text-xs transition-all ${
                        heygenAvatarStyle === s.id
                          ? "border-purple-500/50 bg-purple-500/10 text-white"
                          : "border-white/[0.08] bg-white/[0.03] text-white/50 hover:border-white/20"
                      }`}
                    >
                      <div className="font-medium">{s.label}</div>
                      <div className="text-[10px] text-white/40">{s.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Expressiveness */}
              <div className="space-y-1.5">
                <Label className="text-xs text-white/60">{t("heygen.expressiveness.label")}</Label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    {
                      id: "low" as HeyGenExpressiveness,
                      label: t("heygen.expressiveness.low"),
                      desc: t("heygen.expressiveness.lowDesc"),
                    },
                    {
                      id: "medium" as HeyGenExpressiveness,
                      label: t("heygen.expressiveness.medium"),
                      desc: t("heygen.expressiveness.mediumDesc"),
                    },
                    {
                      id: "high" as HeyGenExpressiveness,
                      label: t("heygen.expressiveness.high"),
                      desc: t("heygen.expressiveness.highDesc"),
                    },
                  ].map((e) => (
                    <button
                      key={e.id}
                      onClick={() => setHeygenExpressiveness(e.id)}
                      className={`px-2 py-2 rounded-lg border text-xs transition-all ${
                        heygenExpressiveness === e.id
                          ? "border-purple-500/50 bg-purple-500/10 text-white"
                          : "border-white/[0.08] bg-white/[0.03] text-white/50 hover:border-white/20"
                      }`}
                    >
                      <div className="font-medium">{e.label}</div>
                      <div className="text-[10px] text-white/40">{e.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Caption toggle */}
              <div className="flex items-center justify-between rounded-lg border border-white/[0.08] bg-white/[0.03] px-3 py-2">
                <div>
                  <Label className="text-xs text-white/80">{t("heygen.caption.label")}</Label>
                  <p className="text-[10px] text-white/40 mt-0.5">{t("heygen.caption.desc")}</p>
                </div>
                <button
                  onClick={() => setHeygenCaption((v) => !v)}
                  className={`relative w-10 h-5 rounded-full transition-colors ${heygenCaption ? "bg-purple-500" : "bg-white/10"}`}
                  aria-label="toggle caption"
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${heygenCaption ? "translate-x-5" : ""}`}
                  />
                </button>
              </div>

              {/* Script */}
              <div className="space-y-1.5">
                <Label className="text-xs text-white/60">
                  {t("heygen.script.label")}{" "}
                  <span className="text-white/30">{t("heygen.script.hint")}</span>
                </Label>
                <Textarea
                  value={heygenScript}
                  onChange={(e) => setHeygenScript(e.target.value.slice(0, 5000))}
                  placeholder={t("heygen.script.placeholder")}
                  className="text-xs bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/30 resize-none h-32"
                />
                <div className="text-[10px] text-white/30 text-right">
                  {heygenScript.length}/5000
                </div>
              </div>
            </div>
          )}

          {/* Negative prompt */}
          {!isSeedance && !isMotion && !isEdit && (
            <div className="space-y-1.5">
              <Label className="text-xs text-white/60">
                {t("negativePrompt.label")}{" "}
                <span className="text-white/30">{t("negativePrompt.hint")}</span>
              </Label>
              <Textarea
                value={negativePrompt}
                onChange={(e) => setNegativePrompt(e.target.value)}
                placeholder={t("negativePrompt.placeholder")}
                className="text-xs bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/30 resize-none h-16"
              />
            </div>
          )}

          {/* Token cost + generate */}
          <div className="space-y-2 pt-1">
            <div className="flex items-center gap-1.5 text-[10px] text-white/30">
              <Zap className="w-3 h-3 text-yellow-500/60" />
              {isHeygen ? (
                <>
                  <span>{t("generate.costHeygen")}</span>
                  <span className="text-white/20">·</span>
                  <span>{t("generate.renderHeygen")}</span>
                </>
              ) : (
                <>
                  <span>{t("generate.cost", { tokens: tokenCost.toLocaleString() })}</span>
                  <span className="text-white/20">·</span>
                  <span>{t("generate.time")}</span>
                </>
              )}
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleGenerate}
                disabled={
                  isGenerating ||
                  (isHeygen
                    ? !heygenAvatarId || !heygenVoiceId || !heygenScript.trim()
                    : isEdit
                      ? !prompt.trim() || !editVideoUrl
                      : (!prompt.trim() && !isMotion) ||
                        (isMotion &&
                          (!motionCharacterImage || !motionDrivingVideo || !motionConsent)))
                }
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl text-sm font-semibold transition bg-red-500 hover:bg-red-400 text-white shadow-lg shadow-red-500/20 disabled:bg-white/[0.06] disabled:text-white/40 disabled:shadow-none disabled:cursor-not-allowed"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t("generate.button")}
                  </>
                ) : (
                  <>
                    {t("generate.button")}
                    <Sparkles className="w-3.5 h-3.5" />
                    {!isHeygen && (
                      <span className="opacity-80 text-xs font-normal">
                        ⌘ {tokenCost.toLocaleString()}
                      </span>
                    )}
                  </>
                )}
              </button>
              {isGenerating && (
                <Button
                  onClick={handleCancel}
                  variant="outline"
                  size="sm"
                  className="text-xs border-white/[0.1] bg-white/[0.04] text-white hover:bg-white/[0.08]"
                >
                  {t("generate.cancel")}
                </Button>
              )}
            </div>

            <StatusBadge state={generationState} />
          </div>
        </div>

        {/* Right: Results / History / How it works */}
        <div className="space-y-3">
          <div className="flex items-center gap-1.5 border-b border-white/[0.06] pb-2">
            <button
              onClick={() => setResultsTab("current")}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-medium transition-colors ${
                resultsTab === "current"
                  ? "bg-white/[0.08] text-white"
                  : "text-white/40 hover:text-white/70"
              }`}
            >
              <Film className="w-3 h-3" />
              {t("results.currentTitle")}
            </button>
            <button
              onClick={() => setResultsTab("history")}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-medium transition-colors ${
                resultsTab === "history"
                  ? "bg-white/[0.08] text-white"
                  : "text-white/40 hover:text-white/70"
              }`}
            >
              <History className="w-3 h-3" />
              {t("results.historyButton")}
            </button>
            <button
              onClick={() => setResultsTab("how-it-works")}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-medium transition-colors ${
                resultsTab === "how-it-works"
                  ? "bg-white/[0.08] text-white"
                  : "text-white/40 hover:text-white/70"
              }`}
            >
              <BookOpen className="w-3 h-3" />
              Jak to działa
            </button>
          </div>

          {resultsTab === "how-it-works" ? (
            <HowItWorksInfographic />
          ) : historyView ? (
            historyLoading ? (
              <div className="flex items-center justify-center py-16 gap-2 text-xs text-white/30">
                <Loader2 className="w-4 h-4 animate-spin" />
                {t("results.loadingHistory")}
              </div>
            ) : !historyData?.items?.length ? (
              <div className="flex flex-col items-center justify-center py-16 border border-dashed border-white/[0.08] rounded-xl gap-3 text-center">
                <History className="w-10 h-10 text-white/10" />
                <p className="text-xs text-white/30">{t("results.emptyHistory")}</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {historyData.items.map(
                  (item) =>
                    item.signedUrl && (
                      <div
                        key={item.id}
                        className="rounded-xl border border-white/[0.08] bg-[#0d0f14] overflow-hidden"
                      >
                        <div className="relative bg-black/40 aspect-video flex items-center justify-center">
                          <video
                            src={item.signedUrl}
                            controls
                            className="w-full h-full object-contain"
                            preload="metadata"
                          />
                        </div>
                        <div className="p-3 space-y-1.5">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-1.5 text-xs text-white/50">
                              <Film className="w-3 h-3" />
                              <span>{item.imageModel ?? "—"}</span>
                              {item.fileSizeBytes && (
                                <>
                                  <span>·</span>
                                  <span>{formatBytes(item.fileSizeBytes)}</span>
                                </>
                              )}
                            </div>
                            <a
                              href={item.signedUrl}
                              download={`video-ad-${item.id.slice(0, 8)}.mp4`}
                              className="p-1.5 rounded text-white/30 hover:text-white/70 transition-colors"
                              title={t("results.downloadTooltip")}
                            >
                              <Download className="w-3.5 h-3.5" />
                            </a>
                          </div>
                          {item.prompt && (
                            <p className="text-[10px] text-white/30 leading-relaxed line-clamp-2">
                              {item.prompt}
                            </p>
                          )}
                          {item.createdAt && (
                            <p className="text-[9px] text-white/20">
                              {new Date(item.createdAt).toLocaleDateString("pl-PL", {
                                day: "numeric",
                                month: "short",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </p>
                          )}
                        </div>
                      </div>
                    )
                )}
              </div>
            )
          ) : generatedVideos.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 border border-dashed border-white/[0.08] rounded-xl gap-3 text-center">
              <Film className="w-10 h-10 text-white/10" />
              <p className="text-xs text-white/30">{t("results.emptyVideos")}</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {generatedVideos.map((v) => (
                <VideoCard key={v.id} video={v} onDelete={handleDeleteVideo} />
              ))}
            </div>
          )}
        </div>
      </div>

      <Dialog open={modelDialogOpen} onOpenChange={setModelDialogOpen}>
        <DialogContent className="bg-[#0d0f14] border-white/[0.08] max-w-md p-0 gap-0 overflow-hidden">
          <DialogHeader className="px-4 pt-4 pb-2">
            <DialogTitle className="text-sm text-white">Wybierz model</DialogTitle>
          </DialogHeader>
          <div className="px-4 pb-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
              <input
                type="text"
                value={modelSearch}
                onChange={(e) => setModelSearch(e.target.value)}
                placeholder="Szukaj..."
                className="w-full pl-9 pr-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-white/[0.15]"
              />
            </div>
          </div>
          <div className="max-h-[60vh] overflow-y-auto px-2 pb-3 space-y-1">
            {VIDEO_MODELS.filter((m) => {
              if (m.category !== topTab) return false;
              const q = modelSearch.toLowerCase().trim();
              if (!q) return true;
              const badge = m.badge
                ? (m.badge.startsWith("models.") ? t(m.badge) : m.badge).toLowerCase()
                : "";
              return m.label.toLowerCase().includes(q) || badge.includes(q);
            }).map((m) => (
              <button
                key={m.id}
                onClick={() => {
                  handleModelChange(m.id);
                  setTopTab(m.category);
                  setModelDialogOpen(false);
                  setModelSearch("");
                }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition ${
                  selectedModel === m.id
                    ? "bg-red-500/10 border border-red-500/30"
                    : "hover:bg-white/[0.04] border border-transparent"
                }`}
              >
                <div
                  className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                    selectedModel === m.id
                      ? "bg-red-500/20 text-red-400"
                      : "bg-white/[0.06] text-white/50"
                  }`}
                >
                  <Film className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white font-medium truncate">{m.label}</div>
                  {m.badge && (
                    <div className="text-[10px] text-white/40 truncate mt-0.5">
                      {m.badge.startsWith("models.") ? t(m.badge) : m.badge}
                    </div>
                  )}
                </div>
                <div className="text-[10px] text-white/30 shrink-0">
                  {m.minDuration === m.maxDuration
                    ? `${m.minDuration}s`
                    : `${m.minDuration}-${m.maxDuration}s`}
                </div>
                {selectedModel === m.id && <Check className="w-4 h-4 text-red-400 shrink-0" />}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={libraryPickerOpen} onOpenChange={setLibraryPickerOpen}>
        <DialogContent className="bg-[#0d0f14] border-white/[0.08] max-w-2xl p-0 gap-0 overflow-hidden">
          <DialogHeader className="px-4 pt-4 pb-2">
            <DialogTitle className="text-sm text-white">Wybierz z biblioteki</DialogTitle>
          </DialogHeader>
          <div className="p-3 max-h-[60vh] overflow-y-auto">
            {libraryLoading ? (
              <div className="py-12 text-center">
                <Loader2 className="animate-spin mx-auto text-white/30" size={24} />
              </div>
            ) : !libraryData?.items?.length ? (
              <p className="py-12 text-center text-xs text-white/40">Biblioteka jest pusta</p>
            ) : (
              <div className="grid grid-cols-4 gap-2">
                {libraryData.items
                  .filter((item) => {
                    if (!item.signedUrl || !item.mediaType) return false;
                    if (item.mediaType === "image")
                      return isMotion || isSeedance || selectedModel === "kling-2.1" || isVeo;
                    if (item.mediaType === "video") return isMotion || isSeedance;
                    return false;
                  })
                  .map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleLibrarySelect(item)}
                      className="aspect-square rounded-lg bg-white/[0.04] border border-white/[0.06] overflow-hidden hover:border-red-500/50 hover:scale-105 transition-all relative"
                    >
                      <img
                        src={item.signedUrl!}
                        alt={item.filename}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                      {item.mediaType === "video" && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <Video size={16} className="text-white/80" />
                        </div>
                      )}
                    </button>
                  ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function HowItWorksInfographic() {
  return (
    <div className="space-y-5 pt-2">
      <div>
        <h2 className="text-xl md:text-2xl font-extrabold text-white tracking-tight">
          TWÓRZ WIDEO JEDNYM KLIKNIĘCIEM
        </h2>
        <p className="text-[11px] text-white/40 mt-2 leading-relaxed">
          Wybierz model, opisz scenę i kliknij Generuj — AI stworzy gotowy film reklamowy. Możesz
          dodać obraz startowy, aby sterować pierwszą klatką.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3">
        <InfographicStep
          step={1}
          title="WYBIERZ MODEL"
          description="Veo, Seedance, Kling lub Motion Control — każdy do innego stylu reklamy."
          visual={
            <div className="relative h-28 flex items-center justify-center gap-1">
              <div className="w-16 h-20 rounded-lg bg-gradient-to-br from-amber-700/60 to-amber-900/60 border border-white/10 shadow opacity-50" />
              <div className="w-20 h-24 rounded-lg bg-gradient-to-br from-sky-500/60 to-indigo-600/60 border-2 border-red-500 shadow-xl shadow-red-500/20 z-10 flex items-end justify-center pb-1.5">
                <span className="text-[8px] font-bold text-white tracking-wider">CINEMATIC</span>
              </div>
              <div className="w-16 h-20 rounded-lg bg-gradient-to-br from-zinc-600/60 to-zinc-800/60 border border-white/10 shadow opacity-50" />
              <MousePointerClick size={14} className="absolute right-8 bottom-2 text-white/80" />
            </div>
          }
        />
        <InfographicStep
          step={2}
          title="DODAJ PROMPT I OBRAZ"
          description="Opisz wideo lub wgraj obraz referencyjny do animacji."
          visual={
            <div className="relative h-28 flex items-center justify-center">
              <div className="absolute -left-1 top-4 w-24 h-16 rounded-lg bg-gradient-to-br from-pink-500/40 via-orange-500/40 to-yellow-500/40 rotate-[-10deg] border border-white/10 shadow-xl flex items-center justify-center">
                <Hand size={16} className="text-white/80" />
              </div>
              <div className="absolute right-3 bottom-0 w-32 h-20 rounded-lg border-2 border-dashed border-white/20 bg-white/[0.02] flex flex-col items-center justify-center gap-1 backdrop-blur">
                <ImageIcon size={16} className="text-white/40" />
                <span className="text-[9px] font-semibold text-white/70 tracking-wider">WGRAJ</span>
              </div>
            </div>
          }
        />
        <InfographicStep
          step={3}
          title="ODBIERZ WIDEO"
          description="Kliknij Generuj — gotowe wideo pojawi się w zakładce Aktualne."
          visual={
            <div className="relative h-28 flex items-center justify-center">
              <div className="w-32 h-20 rounded-xl bg-gradient-to-br from-emerald-500/30 via-teal-500/30 to-cyan-500/30 border-2 border-white shadow-xl flex items-center justify-center">
                <Film size={22} className="text-white/90" />
              </div>
              <Sparkles size={14} className="absolute top-2 right-12 text-amber-300" />
            </div>
          }
        />
      </div>
    </div>
  );
}

function InfographicStep({
  step,
  title,
  description,
  visual,
}: {
  step: number;
  title: string;
  description: string;
  visual: React.ReactNode;
}) {
  return (
    <div className="relative rounded-xl bg-white/[0.02] border border-white/[0.06] p-4 hover:border-white/[0.12] transition">
      <div className="absolute top-3 left-3 w-6 h-6 rounded-full bg-red-500/20 text-red-400 flex items-center justify-center text-[11px] font-bold z-10">
        {step}
      </div>
      <div className="rounded-lg bg-black/40 border border-white/[0.04] overflow-hidden mb-3 mt-1">
        {visual}
      </div>
      <div className="text-sm font-bold text-white tracking-wide">{title}</div>
      <p className="text-[11px] text-white/50 mt-1 leading-relaxed">{description}</p>
    </div>
  );
}
